import { Component } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DataService } from '../../services/data.service'; // Importa il servizio
import { FunctionService } from '../../services/function.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { applicationRoutes } from '../../app.routes';
import { ShipmentManagementService } from '../../services/shipment-management.service';
import { DamageManagementService } from '../../services/damage-management.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [TranslateModule, CommonModule, RouterModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent {
  constructor(
    private dataService: DataService,
    public functionService: FunctionService,
    private authService: AuthService,
    private router: Router,
    private shipmentManagementService: ShipmentManagementService,
    private damageManagementService: DamageManagementService
  ) {}
  dashboardData: any; // Proprietà per salvare i dati
    hasVisibleCards = false;

    // Numero di bolle in sospeso (warning sulla card Gestione Spedizioni)
    pendingWaybillsCount = 0;
    isPendingWaybillsLoading = false;

    // Badge della card Danni: pratiche ancora da chiudere
    openDamagesCount = 0;
    isOpenDamagesLoading = false;

    // Badge espanso (chiave della card) per mostrare il dettaglio dei contatori
    expandedBadge: string | null = null;

  // Elenco delle card presenti in dashboard (le stesse chiavi usate nei *ngIf)
  private readonly dashboardFeatures = [
    'statistics',
    'overview',
    'returns',
    'returns-history',
    'returns-total',
    'shipment-request',
    'return-request',
    'anagraphic',
    'shipment-management',
    // Gestione Danni (/damage-management): card commentata nel template, rotta ancora
    // attiva via link diretto. Fuori dall'elenco così chi ha solo "damages" non
    // viene rediretto automaticamente a /damage-management
    // 'damages',
    // TODO: Finance non ancora rilasciato - card nascosta
    // 'finance',
    'images',
    'system',
    'loading-list',
    'overview-warehouse',
    'trip-management',
    'overview-picking',
    'stock'
  ];

  ngOnInit(): void {
    // Preleva i dati dal DataService
    this.dashboardData = this.dataService.getData('function') || {
      title: 'Titolo di default',
      description: 'Descrizione di default',
      stats: { users: 0, orders: 0 }
    };

    // Esempio: Imposta dati se non esistono
    if (!this.dataService.hasData('function')) {
      this.dataService.setData('function', this.dashboardData);
    }

    // Controllo visibilità card
    this.hasVisibleCards = this.dashboardFeatures.some(f =>
      this.functionService.canRead(f)
    );

    // Se non c'è nulla di visibile, effettua logout e vai al login
    if (!this.hasVisibleCards) {
      this.authService.logout();        // supponendo che svuoti token/sessione
      this.router.navigate(['/login']); // opzionale, se non lo fa già logout()
    }

    // Se la funzione Gestione Spedizioni è attiva, recupera il numero di bolle in sospeso
    if (this.functionService.canRead('shipment-management')) {
      this.isPendingWaybillsLoading = true;
      this.shipmentManagementService.getWarningPendingCount()
        .pipe(finalize(() => this.isPendingWaybillsLoading = false))
        .subscribe(count => {
          this.pendingWaybillsCount = count;
        });
    }

    // Se la funzione Danni è attiva, recupera il numero di pratiche da chiudere
    if (this.functionService.canRead('damages')) {
      this.isOpenDamagesLoading = true;
      this.damageManagementService.getWarningCounters()
        .pipe(finalize(() => this.isOpenDamagesLoading = false))
        .subscribe(counters => {
          this.openDamagesCount = counters.openDamagesCount;
        });
    }

    const availableFeatures = this.dashboardFeatures.filter(f =>
      this.functionService.canRead(f)
    );

    if (availableFeatures.length === 1) {

      const route = this.getRouteByFunctionName(
        availableFeatures[0]
      );

      if (route) {
        this.router.navigate([route]);
        return;
      }
    }
  }

  /**
   * Apre o chiude il pannello di dettaglio del badge di una card. Il badge sta
   * dentro il link della card: ferma l'evento per non navigare alla pagina.
   */
  toggleBadge(event: Event, key: string): void {
    this.stopCardNavigation(event);
    this.expandedBadge = this.expandedBadge === key ? null : key;
  }

  /** Impedisce che un click dentro il badge o il suo dettaglio apra la pagina della card. */
  stopCardNavigation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private getRouteByFunctionName(functionName: string): string | null {

    for (const route of applicationRoutes) {

      if (!route.children) {
        continue;
      }

      const child = route.children.find(
        c => c.data?.['functionName'] === functionName
      );

      if (child) {
        return child.path ?? null;
      }
    }

    return null;
  }
}